import React, { useState, useEffect } from 'react'
import Avatar from '@atlaskit/avatar'
import Lozenge from '@atlaskit/lozenge'
import Tooltip from '@atlaskit/tooltip'
import MoreIcon from '@atlaskit/icon/glyph/more'
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler'
import DocumentIcon from '@atlaskit/icon/glyph/document'
import Button from '@atlaskit/button'
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'
import { getUserDetails, getCachedUserDetails } from '../../utils/userCache'

/**
 * LIBRARY SCENARIO CARD COMPONENT
 *
 * Displays a library test scenario (template) as a flat backlog row,
 * matching the release ScenarioCard visual style.
 *
 * Features:
 * - Single-line layout: icon, key, name, priority, avatar, case count, actions
 * - Flat row with bottom divider only (no card borders/shadows)
 * - Subtle background change on hover
 * - Click to navigate to scenario detail page
 */
function LibraryScenarioCard({
  scenario,
  onClick,
  onEdit,
  onMove,
  onDelete,
  onClone,
  dragHandleProps,
  // When true, the handle container is still rendered (to keep the left spacing consistent
  // across cards), but the icon is invisible, the cursor is default, and drag listeners
  // are not attached. Set by the parent panel when the visible list does not map 1:1 to
  // the DB's folder-scoped orderIndex (All Library Scenarios, active filters, or non-default sort).
  isDragDisabled = false,
  isDragging = false
}) {
  // Extract scenario data
  const {
    id,
    scenarioName,
    scenarioKey,
    description,
    priority = 'Medium',
    assignee,
    testCaseCount = 0
  } = scenario

  const [isHovered, setIsHovered] = useState(false)
  // Track dropdown menu open state to prevent menu from disappearing
  // when mouse briefly leaves the row while navigating to dropdown items
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // Initialize with cached value for instant render if available
  const [userInfo, setUserInfo] = useState(() => {
    if (assignee && typeof assignee === 'object' && assignee.displayName) {
      return assignee
    }
    if (assignee && typeof assignee === 'string') {
      return getCachedUserDetails(assignee)
    }
    return null
  })

  // Fetch user details when assignee is an account ID string
  // Uses cache-first approach - parent panel prefetches users in bulk
  useEffect(() => {
    // If assignee is already an object with displayName, use it directly
    if (assignee && typeof assignee === 'object' && assignee.displayName) {
      setUserInfo(assignee)
      return
    }
    // If assignee is a string (account ID), fetch from cache or API
    if (assignee && typeof assignee === 'string') {
      getUserDetails(assignee).then(user => {
        if (user) {
          setUserInfo(user)
        } else {
          setUserInfo({ accountId: assignee, displayName: 'Unknown User', avatarUrl: null })
        }
      }).catch(() => {
        setUserInfo({ accountId: assignee, displayName: 'Unknown User', avatarUrl: null })
      })
    } else {
      // No assignee - unassigned
      setUserInfo(null)
    }
  }, [assignee])

  // Get priority lozenge appearance
  const getPriorityAppearance = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'removed'
      case 'High':
        return 'moved'
      case 'Medium':
        return 'inprogress'
      case 'Low':
        return 'new'
      default:
        return 'default'
    }
  }

  return (
    <div
      className="library-scenario-card"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        /* Flat backlog row: no border-radius, no box border, only bottom divider */
        backgroundColor: (isHovered || isMenuOpen)
          ? 'var(--ds-background-neutral-subtle-hovered, #F4F5F7)'
          : 'var(--ds-surface, #FFFFFF)',
        borderRadius: '0',
        border: 'none',
        borderBottom: '1px solid var(--ds-border, #DFE1E6)',
        boxShadow: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease'
      }}
    >
      {/* Single-row layout: drag handle + content all on one line */}
      <div style={{ padding: '6px 12px 6px 0', display: 'flex', gap: '0', alignItems: 'center' }}>
        {/* Drag Handle — flush left, appears on hover when drag is enabled.
            When isDragDisabled is true, we still render this container so the left
            spacing stays identical to draggable cards, but we omit the drag listeners
            and force the icon to stay invisible (no hover reveal). */}
        {dragHandleProps && (
          <div
            {...(isDragDisabled ? {} : dragHandleProps)}
            className="drag-handle"
            style={{
              cursor: isDragDisabled ? 'default' : (isDragging ? 'grabbing' : 'grab'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: !isDragDisabled && (isHovered || isMenuOpen) ? 1 : 0,
              transition: 'opacity 0.15s ease',
              touchAction: 'none',
              padding: '0 2px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DragHandlerIcon
              label="Drag to reorder"
              size="medium"
              primaryColor="var(--ds-text-subtlest, #6B778C)"
            />
          </div>
        )}

        {/* Single Row: [icon] [key] [name...] | [priority] [avatar] [counts] [actions] */}
        <div style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {/* Scenario type icon */}
          <DocumentIcon
            label="Scenario"
            size="small"
            primaryColor="var(--ds-icon-brand, #0052CC)"
          />

          {/* Scenario Key (if available) - uses --ds-font-body token with monospace override */}
          {scenarioKey && (
            <span style={{
              font: 'var(--ds-font-body)',
              fontFamily: 'monospace',
              fontWeight: 500,
              letterSpacing: '-0.003em',
              color: 'var(--ds-text-subtlest, #6B778C)',
              flexShrink: 0
            }}>
              {scenarioKey}
            </span>
          )}

          {/* Scenario Name - uses --ds-font-body token to match details page typography */}
          <span style={{
            flex: 1,
            font: 'var(--ds-font-body)',
            fontWeight: 500,
            letterSpacing: '-0.003em',
            color: 'var(--ds-text, #172B4D)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {scenarioName}
          </span>

          {/* Right-aligned metadata section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
            marginLeft: 'auto'
          }}>
            {/* Priority lozenge - fixed-width container for alignment */}
            <div style={{ width: '70px', flexShrink: 0, display: 'flex', justifyContent: 'flex-start' }}>
              <Lozenge appearance={getPriorityAppearance(priority)}>
                {priority}
              </Lozenge>
            </div>

            {/* Assignee avatar with tooltip */}
            <Tooltip content={userInfo?.displayName || 'Unassigned'}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  size="xsmall"
                  src={userInfo?.avatarUrl || null}
                  name={userInfo?.displayName || 'Unassigned'}
                />
              </div>
            </Tooltip>

            {/* Test case count - fixed-width for alignment */}
            <span style={{
              fontSize: '11px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              width: '52px',
              textAlign: 'left',
              flexShrink: 0
            }}>
              {testCaseCount} {testCaseCount !== 1 ? 'cases' : 'case'}
            </span>
          </div>

          {/* Actions Menu - visible on hover */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              opacity: (isHovered || isMenuOpen) ? 1 : 0,
              transition: 'opacity 0.15s ease',
              pointerEvents: (isHovered || isMenuOpen) ? 'auto' : 'none',
              flexShrink: 0
            }}
          >
            <DropdownMenu
              trigger={({ triggerRef, ...props }) => (
                <Button
                  {...props}
                  ref={triggerRef}
                  appearance="subtle"
                  spacing="compact"
                  iconBefore={<MoreIcon label="More actions" size="small" />}
                />
              )}
              shouldRenderToParent
              onOpenChange={({ isOpen }) => setIsMenuOpen(isOpen)}
            >
              <DropdownItemGroup>
                <DropdownItem onClick={() => onEdit?.(id)}>Edit</DropdownItem>
                <DropdownItem onClick={() => onMove?.(id)}>Move to Folder</DropdownItem>
                <DropdownItem onClick={() => onClone?.(id)}>Clone</DropdownItem>
                <DropdownItem onClick={() => onDelete?.(id)}>Delete</DropdownItem>
              </DropdownItemGroup>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  )
}

export { LibraryScenarioCard }
